import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import {
  cleanupPreviewModuleCaches,
  installGlobalModuleLoadRecovery,
  isPreviewOrIframeContext,
} from "./lib/moduleLoadRecovery";
import { preserveBootstrapLoader } from "./lib/bootstrapLoader";
import { initWebVitals } from "./lib/webVitals";
import "./index.css";

/**
 * In preview/iframe contexts (Lovable editor), unregister any stale
 * Workbox service workers that may serve outdated cached chunks.
 * The push SW (sw-push.js) is NOT affected — it only handles push events.
 */
async function guardServiceWorkers() {
  if (!isPreviewOrIframeContext()) return;
  if (!("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      // Keep the push SW alive — only kill Workbox/PWA SWs
      const swUrl = reg.active?.scriptURL ?? reg.installing?.scriptURL ?? "";
      if (swUrl.includes("sw-push")) continue;
      await reg.unregister();
    }
  } catch {
    // Ignore — best-effort cleanup
  }
}

function bootstrap() {
  installGlobalModuleLoadRecovery();

  const rootElement = document.getElementById("root");
  preserveBootstrapLoader(rootElement);

  // PERF: Detect auth state from localStorage to prefetch ONLY the chunks
  // we actually need on the first route. Saves ~1 chunk download on the
  // critical path. Falls back to prefetching both if detection fails.
  let hasSession = false;
  try {
    const projectRef = (import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined) ?? "";
    const key = projectRef ? `sb-${projectRef}-auth-token` : "";
    hasSession = key ? !!localStorage.getItem(key) : false;
  } catch {
    hasSession = false;
  }

  if (hasSession) {
    void import("./pages/Agenda");
    void import("./components/AppLayout");
  } else {
    void import("./pages/Login");
  }

  // PERF: Render IMMEDIATELY. Don't block first paint waiting for cache
  // cleanup — run it in parallel via idle/microtask. The bootstrap watchdog
  // and module-recovery counter still protect against truly stale chunks.
  createRoot(rootElement!).render(<App />);

  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };

  // Defer non-critical work until the browser is idle so it doesn't
  // compete with the initial render's JS execution and hydration.
  const runDeferredWork = () => {
    void cleanupPreviewModuleCaches();
    void guardServiceWorkers();
    initWebVitals();
  };

  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(runDeferredWork, { timeout: 2500 });
  } else {
    setTimeout(runDeferredWork, 800);
  }
}

bootstrap();
